import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useParams } from "react-router-dom";
import Walkthrough from '../Walkthrough/Walkthrough';
import { uploadOriginalToS3, fetchGalleryListByFolderID } from "../../utils/s3";
import { useConnectivity } from '../../hooks/useConnectivity';
import ConnectivityPopup from '../ConnectivityPopup';
import BuyCreditsPage from '../BuyCredits/BuyCreditsPage';
import { storePendingUploads, getPendingUploads, getPendingUploadsMetadata, clearPendingUploads, getPendingUploadsFromIndexedDB, markFolderAsUsedForUpload, isFolderUsedForUpload, getFolderById, storeFileHandles, getFileHandles, clearFileHandles } from '../../services/indexedDB';
import axios from 'axios';
import AWSGallery from '../Gallery/AWSGallery';
import './FolderUpload.css';

const UPLOAD_STEPS = [
  {
    selector: null,
    title: 'Upload Photos 📷',
    description: "This is where you upload photos for a folder. Let's walk through the key sections.",
    placement: 'center',
  },
  {
    selector: '.fu-breadcrumb',
    title: 'Navigate Back',
    description: 'Use the breadcrumb to go back to All Folders or your Events list at any time.',
    placement: 'bottom',
  },
  {
    selector: '.fu-upload-zone',
    title: 'Select Photos',
    description: 'Click here to pick photos from your device. You can select up to 1,000 photos per upload.',
    placement: 'top',
  },
  {
    selector: '.fu-progress-card',
    title: 'Upload Progress',
    description: 'Track each upload in real time — see how many are uploaded, failed, or still remaining.',
    placement: 'top',
  },
  {
    selector: '.fu-gallery-section',
    title: 'Uploaded Photos',
    description: 'Photos you have already uploaded to this folder appear here. Use Refresh to see the latest.',
    placement: 'top',
  },
];

export default function FolderUpload({ eventId, userId }) {

  const { id } = useParams();
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [files, setFiles] = useState([]);
  const { isOnline, wasOffline, isOffline } = useConnectivity();
  const [credits, setCredits] = useState(null);
  const [vendorPhone, setVendorPhone] = useState('');
  const [showBuyCredits, setShowBuyCredits] = useState(false);
  const [showBuyCreditsPage, setShowBuyCreditsPage] = useState(false);
  const [selectedPhotoCount, setSelectedPhotoCount] = useState(0);

  const [uploadStatuses, setUploadStatuses] = useState({});
  const uploadStatusesRef = useRef({});
  const [uploading, setUploading] = useState(false);
  const [uploadPaused, setUploadPaused] = useState(false);
  const [showConnectivityPopup, setShowConnectivityPopup] = useState(false);
  const [pendingUploads, setPendingUploads] = useState([]);
  const [currentUploadIndex, setCurrentUploadIndex] = useState(0);
  const [hasStoredPendingUploads, setHasStoredPendingUploads] = useState(false);
  const [storedFileCount, setStoredFileCount] = useState(0);
  const [folderHasBeenUsed, setFolderHasBeenUsed] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryPage, setGalleryPage] = useState(1);
  const [galleryMaxKnownPage, setGalleryMaxKnownPage] = useState(1);
  const [checkingPhotos, setCheckingPhotos] = useState(true);
  const [resuming, setResuming] = useState(false);
  const [refreshingGallery, setRefreshingGallery] = useState(false);
  const [showPhotoLimitDialog, setShowPhotoLimitDialog] = useState(false);
  const [tooManyCount, setTooManyCount] = useState(0);
  const [hasStoredHandles, setHasStoredHandles] = useState(false);
  const [storedHandleCount, setStoredHandleCount] = useState(0);
  const fileHandlesRef = useRef([]);

  const isProcessingRef = useRef(false);
  const inFlightNamesRef = useRef(new Set());
  const lockIdRef = useRef(null);
  const signedUrlsRef = useRef({});
  const pendingFilesRef = useRef([]);
  const pausedAtIndexRef = useRef(0);
  

  const getEffectiveIds = () => ({
    ev: eventId || (() => { try { return localStorage.getItem('currentEventId'); } catch(_) { return ''; } })() || '',
    uid: userId
      || (() => { try { return localStorage.getItem('uid'); } catch(_) { return ''; } })()
      || (() => { try { return localStorage.getItem('currentUserId'); } catch(_) { return ''; } })()
      || (() => { try { return localStorage.getItem('vendorId'); } catch(_) { return ''; } })()
      || ''
  });


  useEffect(() => {
    const fetchCredits = async () => {
      try {
        const vendorId = localStorage.getItem('vendorId');
        if (!vendorId) return;
        const baseurl = process.env.REACT_APP_API_BASE_URL;
        const apiKey = process.env.REACT_APP_BROWSER_API_KEY;
        const res = await fetch(`${baseurl}/vendor?vendor_id=${encodeURIComponent(vendorId)}`, {
          headers: { 'x-api-key': apiKey },
        });
        if (!res.ok) return;
        const data = await res.json();
        const vendor = data.vendor || data;
        setCredits(vendor.photo_credits ?? 0);
        const rawMobile = Array.isArray(vendor.mobile) ? vendor.mobile[0] : (vendor.mobile || '');
        setVendorPhone(rawMobile);
      } catch (e) {
      }
    };
    fetchCredits();
  }, []);

  const buildUploadMetadata = async () => {
    const { ev, uid } = getEffectiveIds();

    
    const folderRecord = await getFolderById(id).catch(() => null);
    const rawTags = folderRecord?.tag ?? null;
    const tagsValue = Array.isArray(rawTags) ? rawTags : rawTags ? [rawTags] : null;

    const metadata = {
      event_id: ev,
      user_id: uid,
      folder_id: id,
      source: 'photographer',
    };

    try {
      const logoUrl = localStorage.getItem('currentLogoUrl');
      const watermarkUrl = localStorage.getItem('currentWatermarkUrl');
      if (logoUrl) metadata.logo_url = logoUrl;
      if (watermarkUrl) metadata.watermark_url = watermarkUrl;
    } catch (_) {}

    if (tagsValue) metadata.tags = tagsValue;

    return { metadata, folderName: 'uploads' };
  };

  const buildLockKey = () => {
    const { ev, uid } = getEffectiveIds();
    return `ee_upload_lock:${id}:${ev}:${uid}`;
  };
  const acquireUploadLock = () => {
    try {
      const key = buildLockKey();
      const now = Date.now();
      const existing = JSON.parse(localStorage.getItem(key) || 'null');
      if (existing && now - (existing.ts || 0) < 60000) return false;
      const owner = Math.random().toString(36).slice(2);
      localStorage.setItem(key, JSON.stringify({ owner, ts: now }));
      lockIdRef.current = owner;
      return true;
    } catch (_) { return true; }
  };
  const refreshUploadLock = () => {
    try {
      if (!lockIdRef.current) return;
      const key = buildLockKey();
      localStorage.setItem(key, JSON.stringify({ owner: lockIdRef.current, ts: Date.now() }));
    } catch (_) {}
  };
  const releaseUploadLock = () => {
    try {
      const key = buildLockKey();
      const existing = JSON.parse(localStorage.getItem(key) || 'null');
      if (existing && existing.owner === lockIdRef.current) localStorage.removeItem(key);
      lockIdRef.current = null;
    } catch (_) { lockIdRef.current = null; }
  };

  useEffect(() => {
    const checkFolderUsage = async () => {
      setCheckingPhotos(true);
      try {
        const isUsed = await isFolderUsedForUpload(id);
        setFolderHasBeenUsed(isUsed);
        const { ev, uid } = getEffectiveIds();
        if (ev && uid) {
          const result = await fetchGalleryListByFolderID(ev, uid, id, 1, 10);
          setGalleryImages(result.photos);
          setGalleryPage(1);
          setGalleryMaxKnownPage(result.hasMore ? 2 : 1);
        }
      } catch (error) {
        setFolderHasBeenUsed(false);
      } finally {
        setCheckingPhotos(false);
      }
    };
    checkFolderUsage();
  }, [eventId, userId, id]);

  useEffect(() => {
    const checkHandles = async () => {
      try {
        const { ev, uid } = getEffectiveIds();
        const record = await getFileHandles(id, ev, uid);
        if (record && record.handles && record.handles.length > 0) {
          const remaining = record.handles.length - (record.currentIndex || 0);
          if (remaining > 0) {
            fileHandlesRef.current = record.handles;
            setStoredHandleCount(remaining);
            setCurrentUploadIndex(record.currentIndex || 0);
            setUploadStatuses(record.uploadStatuses || {});
            uploadStatusesRef.current = record.uploadStatuses || {};
            setHasStoredHandles(true);
          }
        }
      } catch (e) {
      }
    };
    checkHandles();
  }, [id]);

  useEffect(() => {
    const checkPendingUploads = async () => {
      try {
        const metadataList = await getPendingUploadsMetadata();
        if (!metadataList || metadataList.length === 0) return;

        const { ev, uid } = getEffectiveIds();
        const relevant = metadataList.filter(m =>
          m.folderId === id && m.eventId === ev && m.userId === uid
        );
        const latest = relevant.sort((a, b) => {
          const lenDiff = (b.fileCount || 0) - (a.fileCount || 0);
          if (lenDiff !== 0) return lenDiff;
          return new Date(b.timestamp || 0) - new Date(a.timestamp || 0);
        })[0];

        if (latest && latest.fileCount > 0) {
          setStoredFileCount(latest.fileCount);
          setCurrentUploadIndex(latest.currentIndex || 0);
          setUploadStatuses(latest.uploadStatuses || {});
          uploadStatusesRef.current = latest.uploadStatuses || {};
          setHasStoredPendingUploads(true);
        }
      } catch (error) {
      }
    };
    checkPendingUploads();
  }, [id]);

  const storePendingUploadsInDB = async (files, currentIndex, statusesOverride) => {
    try {
      const { ev, uid } = getEffectiveIds();
      await storePendingUploads({
        folderId: id,
        eventId: ev,
        userId: uid,
        files,
        currentIndex,
        uploadStatuses: statusesOverride ?? uploadStatuses,
        timestamp: new Date().toISOString(),
      });
      setHasStoredPendingUploads(true);
    } catch (error) {
    }
  };

  const pickFilesWithFSA = async () => {
    const { ev, uid } = getEffectiveIds();
    if (!ev || !uid) {
      alert('Please select an event first before uploading.');
      return;
    }
    if (folderHasBeenUsed) {
      alert('This folder has already been used. Please create a new folder.');
      return;
    }
    try {
      const handles = await window.showOpenFilePicker({
        multiple: true,
        types: [{ description: 'Images', accept: { 'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic'] } }],
      });
      if (!handles || handles.length === 0) return;

      if (handles.length > 1000) {
        setTooManyCount(handles.length);
        setShowPhotoLimitDialog(true);
        return;
      }

      // Credits check: 1 credit = 1 photo
      if (credits !== null && handles.length > credits) {
        setSelectedPhotoCount(handles.length);
        setShowBuyCredits(true);
        return;
      }

      fileHandlesRef.current = handles;
      await storeFileHandles({ folderId: id, eventId: ev, userId: uid, handles, currentIndex: 0, uploadStatuses: {} });
      setHasStoredHandles(true);
      setStoredHandleCount(handles.length);

      const files = await Promise.all(handles.map(h => h.getFile()));
      setFiles(files);
      await uploadFiles(files);
    } catch (e) {
    }
  };

  const resumeFromHandles = async () => {
    if (isProcessingRef.current) return;
    const { ev, uid } = getEffectiveIds();
    try {
      const record = await getFileHandles(id, ev, uid);
      if (!record || !record.handles || record.handles.length === 0) return;

      const handles = record.handles;

      // Request permission once using the first handle that needs it —
      // browser grants access to all files from the same picker session together
      const needsPermission = await Promise.all(
        handles.map(h => h.queryPermission({ mode: 'read' }))
      );
      const firstUngranted = handles.find((_, i) => needsPermission[i] !== 'granted');
      if (firstUngranted) {
        const result = await firstUngranted.requestPermission({ mode: 'read' });
        if (result !== 'granted') return;
      }

      const grantedHandles = [];
      for (let i = 0; i < handles.length; i++) {
        const perm = needsPermission[i] === 'granted'
          ? 'granted'
          : await handles[i].queryPermission({ mode: 'read' });
        if (perm === 'granted') grantedHandles.push(handles[i]);
      }
      if (grantedHandles.length === 0) return;

      fileHandlesRef.current = grantedHandles;
      const files = await Promise.all(grantedHandles.map(h => h.getFile()));
      setFiles(files);
      setPendingUploads(files);
      pendingFilesRef.current = files;
      setUploading(true);

      // Fetch fresh signed URLs — previous session's URLs have expired
      try {
        const { metadata, folderName } = await buildUploadMetadata();
        const urlMap = await getSignedUrls(files, folderName, metadata);
        signedUrlsRef.current = urlMap;

      } catch (err) {
        setUploading(false);
        alert(`Failed to prepare upload: ${err.message}`);
        return;
      }

      await processUploadQueue(files, record.currentIndex || 0);
    } catch (e) {
    }
  };

  const getSignedUrls = async (files, folder, metadata = {}) => {
    const baseurl = process.env.REACT_APP_API_BASE_URL;
    const url = `${baseurl}/upload-urls`;
    const headers = { 'Content-Type': 'application/json' };
    const apiKey = process.env.REACT_APP_BROWSER_API_KEY;
    if (apiKey) headers['x-api-key'] = apiKey;

    const BATCH_SIZE = 50;
    const urlMap = {};

    for (let i = 0; i < files.length; i += BATCH_SIZE) {
      const batch = files.slice(i, i + BATCH_SIZE);
      const payload = {
        files: batch.map(f => ({ name: f.name, type: f.type || 'image/jpeg' })),
        folder,
        metadata,
      };

      let resp;
      try {
        resp = await axios.post(url, payload, { headers });
      } catch (err) {
        const status = err?.response?.status;
        const data = err?.response?.data;
        throw new Error(`/upload-urls ${status ?? 'network error'}: ${JSON.stringify(data) || err.message}`);
      }

      for (const item of resp.data.uploadUrls || []) {
        urlMap[item.fileName] = item.url;
      }
    }

    return urlMap;
  };

  const handleSingleUpload = async (file) => {
    try {
      if (isOffline) throw new Error('No internet connection');

      const signedUrl = signedUrlsRef.current[file.name];
      if (!signedUrl) throw new Error(`No signed URL found for ${file.name}`);

      await axios.put(signedUrl, file, {
        headers: { 'Content-Type': file.type || 'image/jpeg' },
        timeout: 120000, // 2-minute per-request timeout; prevents silent hangs on slow networks
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadStatuses(prev => {
            const next = { ...prev, [file.name]: { ...prev[file.name], progress: percentCompleted } };
            uploadStatusesRef.current = next;
            return next;
          });
        },
      });
    } catch (error) {
      const isTransientError =
        error.message === 'No internet connection' ||
        error.message === 'Network Error' ||
        error.name === 'NetworkError' ||
        error.code === 'ERR_NETWORK' ||
        error.code === 'ECONNABORTED' ||  // axios request timeout
        !navigator.onLine;
      if (isTransientError) {
        setUploadStatuses(prev => {
          const next = { ...prev, [file.name]: { progress: 0, status: 'paused' } };
          uploadStatusesRef.current = next;
          return next;
        });
        throw error;
      } else {
        setUploadStatuses(prev => {
          const next = { ...prev, [file.name]: { progress: 0, status: 'failed' } };
          uploadStatusesRef.current = next;
          return next;
        });
      }
    }
  };

  const generateDoneFile = async (uploadedFiles) => {
    const jsonString = JSON.stringify({ images: uploadedFiles.map(f => f.name) }, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const { ev, uid } = getEffectiveIds();
    const file = new File([blob], `${ev}_done.txt`, { type: "text/plain" });
    try {
      await uploadOriginalToS3(file, file.name, process.env.REACT_APP_DUPLICATE_BUCKET_NAME, { event_id: ev, user_id: uid, folderId: id });
    } catch (error) {
    }
  };

  const processUploadQueue = async (selectedFiles, startIndex = 0) => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    try {
      for (let i = startIndex; i < selectedFiles.length; i++) {
        refreshUploadLock();
        const file = selectedFiles[i];
        setCurrentUploadIndex(i);

        const currentStatus = uploadStatusesRef.current[file.name]?.status || uploadStatuses[file.name]?.status;
        if (currentStatus === "success" || currentStatus === "uploading") continue;

        if (isOffline) {
          pausedAtIndexRef.current = i;
          setUploadPaused(true);
          setShowConnectivityPopup(true);
          return;
        }

        try {
          if (inFlightNamesRef.current.has(file.name)) continue;
          inFlightNamesRef.current.add(file.name);

          setUploadStatuses(prev => ({
            ...prev,
            [file.name]: { progress: 0, status: "uploading" }
          }));

          const uploadPromise = (async () => {
            try {
              await handleSingleUpload(file);
            } catch (e) {
              if (e.message === 'No internet connection' || e.message === 'Network Error' ||
                  !navigator.onLine || e.name === 'NetworkError' || e.code === 'ERR_NETWORK' || e.code === 'ECONNABORTED') {
                await new Promise(r => setTimeout(r, 1500));
                await handleSingleUpload(file);
              } else {
                throw e;
              }
            }
          })();
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Upload timeout after 5 minutes')), 5 * 60 * 1000)
          );

          await Promise.race([uploadPromise, timeoutPromise]);

          setUploadStatuses(prev => {
            const next = { ...prev, [file.name]: { progress: 100, status: "success" } };
            uploadStatusesRef.current = next;
            return next;
          });
          try { inFlightNamesRef.current.delete(file.name); } catch(_) {}

          if (fileHandlesRef.current.length > 0) {
            try {
              const { ev, uid } = getEffectiveIds();
              await storeFileHandles({ folderId: id, eventId: ev, userId: uid, handles: fileHandlesRef.current, currentIndex: i + 1, uploadStatuses: uploadStatusesRef.current });
            } catch (_) {}
          }

        } catch (error) {
          if (error.message === 'No internet connection' ||
              error.message === 'Network Error' ||
              error.name === 'NetworkError' ||
              error.code === 'ERR_NETWORK' ||
              error.code === 'ECONNABORTED' ||
              !navigator.onLine ||
              error.message.includes('timeout')) {
            pausedAtIndexRef.current = i;
            setUploadStatuses(prev => {
              const next = { ...prev, [file.name]: { progress: 0, status: 'paused' } };
              uploadStatusesRef.current = next;
              return next;
            });
            setUploadPaused(true);
            setShowConnectivityPopup(true);
            return;
          } else {
            setUploadStatuses(prev => {
              const next = { ...prev, [file.name]: { progress: 0, status: 'failed' } };
              uploadStatusesRef.current = next;
              return next;
            });
          }
        } finally {
          try { inFlightNamesRef.current.delete(file.name); } catch(_) {}
        }
      }

      setUploading(false);
      setUploadPaused(false);
      setShowConnectivityPopup(false);
      setHasStoredPendingUploads(false);
      setCurrentUploadIndex(selectedFiles.length);
      setPendingUploads([]);

      try {
        const { ev, uid } = getEffectiveIds();
        await clearFileHandles(id, ev, uid);
        fileHandlesRef.current = [];
        setHasStoredHandles(false);
      } catch (_) {}

      try {
        await markFolderAsUsedForUpload(id);
        setFolderHasBeenUsed(true);
      } catch (error) {
      }

      await generateDoneFile(selectedFiles);
    } finally {
      isProcessingRef.current = false;
      releaseUploadLock();
    }
  };

  const resumeUploads = async () => {
    if (isProcessingRef.current) return;

    let filesToResume = pendingUploads;
    if (filesToResume.length === 0 && hasStoredPendingUploads) {
      const storedUploads = await getPendingUploads();
      const { ev, uid } = getEffectiveIds();
      const relevant = storedUploads.filter(u =>
        u.folderId === id && u.eventId === ev && u.userId === uid
      );
      const latest = relevant.sort((a, b) => {
        const lenDiff = (b.files?.length || 0) - (a.files?.length || 0);
        if (lenDiff !== 0) return lenDiff;
        return new Date(b.timestamp || 0) - new Date(a.timestamp || 0);
      })[0];
      if (latest?.files?.length > 0) {
        filesToResume = latest.files;
        setFiles(latest.files);
        setPendingUploads(latest.files);
      }
    }

    if (filesToResume.length > 0 && isOnline) {
      pendingFilesRef.current = filesToResume;
      const normalized = { ...uploadStatuses };
      for (let i = currentUploadIndex; i < filesToResume.length; i++) {
        const name = filesToResume[i].name;
        if (normalized[name]?.status === 'paused' || normalized[name]?.status === 'saved_offline') {
          normalized[name] = { progress: 0, status: 'pending' };
        }
      }
      setUploadStatuses(normalized);
      uploadStatusesRef.current = normalized;
      try { await storePendingUploadsInDB(filesToResume, currentUploadIndex, normalized); } catch (_) {}

      try {
        const swUploads = await getPendingUploadsFromIndexedDB();
        const swFiles = swUploads
          .filter(u => u.metadata?.folderId === id && u.metadata?.eventId === eventId && u.metadata?.userId === userId)
          .map(u => u.file);
        if (swFiles.length > 0) {
          const existingNames = new Set(filesToResume.map(f => f.name));
          const newOnes = swFiles.filter(f => !existingNames.has(f.name));
          if (newOnes.length > 0) {
            filesToResume = [...filesToResume, ...newOnes];
            setPendingUploads(filesToResume);
            await storePendingUploadsInDB(filesToResume, currentUploadIndex, normalized);
          }
        }
      } catch (e) {
      }

      // Fetch fresh signed URLs — previous session's URLs have expired
      try {
        const { metadata, folderName } = await buildUploadMetadata();
        const urlMap = await getSignedUrls(filesToResume, folderName, metadata);
        signedUrlsRef.current = urlMap;

      } catch (err) {
        alert('Failed to prepare upload. Please try again.');
        return;
      }

      await processUploadQueue(filesToResume, currentUploadIndex);
    }
  };

  useEffect(() => {
    if (isOffline && uploading) {
      setUploadPaused(true);
      setShowConnectivityPopup(true);
    } else if (isOnline && uploadPaused) {
      setUploadPaused(false);
      setShowConnectivityPopup(false);
      // Auto-resume: restart the queue using refs so state timing doesn't matter
      if (uploading && !isProcessingRef.current && pendingFilesRef.current.length > 0) {
        (async () => {
          try {
            const filesToResume = pendingFilesRef.current;
            const startIdx = pausedAtIndexRef.current;
            const normalized = { ...uploadStatusesRef.current };
            for (let i = startIdx; i < filesToResume.length; i++) {
              const name = filesToResume[i].name;
              if (normalized[name]?.status === 'paused' || normalized[name]?.status === 'saved_offline') {
                normalized[name] = { progress: 0, status: 'pending' };
              }
            }
            setUploadStatuses(normalized);
            uploadStatusesRef.current = normalized;
            // Always refresh signed URLs — they expire and must not be reused
            const { metadata, folderName } = await buildUploadMetadata();
            const urlMap = await getSignedUrls(filesToResume, folderName, metadata);
            signedUrlsRef.current = urlMap;
            await processUploadQueue(filesToResume, startIdx);
          } catch (_) {
            // If we couldn't fetch signed URLs or the queue failed, re-enter paused state
            // after a short delay so the connectivity effect can retry automatically
            setTimeout(() => setUploadPaused(true), 3000);
          }
        })();
      }
    }
  }, [isOnline, isOffline, uploading, uploadPaused]); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadFiles = async (selectedFiles) => {
    if (isProcessingRef.current) return;
    if (!acquireUploadLock()) return;

    setUploading(true);
    setUploadPaused(false);
    setCurrentUploadIndex(0);
    setPendingUploads(selectedFiles);
    pendingFilesRef.current = selectedFiles;
    pausedAtIndexRef.current = 0;

    const newStatuses = {};
    for (const file of selectedFiles) {
      newStatuses[file.name] = { progress: 0, status: 'pending' };
    }
    setUploadStatuses(() => {
      uploadStatusesRef.current = { ...newStatuses };
      return { ...newStatuses };
    });

    try {
      const { metadata, folderName } = await buildUploadMetadata();
      const urlMap = await getSignedUrls(selectedFiles, folderName, metadata);
      signedUrlsRef.current = urlMap;
    } catch (err) {
      setUploading(false);
      releaseUploadLock();
      alert(`Failed to prepare upload: ${err.message}`);
      return;
    }

    await processUploadQueue(selectedFiles, 0);
  };

  const retryFailedUploads = async () => {
    if (!isOnline) {
      setShowConnectivityPopup(true);
      return;
    }
    const failedFiles = files.filter(file => uploadStatuses[file.name]?.status === 'failed');
    if (failedFiles.length > 0) {
      setUploading(true);
      setPendingUploads(failedFiles);
      pendingFilesRef.current = failedFiles;
      pausedAtIndexRef.current = 0;
      setCurrentUploadIndex(0);
      try {
        const { metadata, folderName } = await buildUploadMetadata();
        const urlMap = await getSignedUrls(failedFiles, folderName, metadata);
        signedUrlsRef.current = urlMap;
      } catch (err) {
        setUploading(false);
        return;
      }
      await processUploadQueue(failedFiles);
    }
  };

  const handleConnectivityRetry = () => {
    setShowConnectivityPopup(false);
    if (isOnline && uploadPaused) resumeUploads();
  };


  const refreshGallery = async () => {
    setRefreshingGallery(true);
    try {
      const { ev, uid } = getEffectiveIds();
      if (ev && uid) {
        const result = await fetchGalleryListByFolderID(ev, uid, id, 1, 10);
        setGalleryImages(result.photos);
        setGalleryPage(1);
        setGalleryMaxKnownPage(result.hasMore ? 2 : 1);
      }
    } catch (e) {
    } finally {
      setRefreshingGallery(false);
    }
  };

  const goToGalleryPage = async (pageNum) => {
    try {
      const { ev, uid } = getEffectiveIds();
      const result = await fetchGalleryListByFolderID(ev, uid, id, pageNum, 10);
      setGalleryImages(result.photos);
      setGalleryPage(pageNum);
      setGalleryMaxKnownPage(prev => result.hasMore ? Math.max(prev, pageNum + 1) : Math.max(prev, pageNum));
    } catch (e) {
    }
  };

  const [showFailedList, setShowFailedList] = useState(false);

  const total = files.length;
  const successCount = Object.values(uploadStatuses).filter(s => s.status === 'success').length;
  const displayedSuccessCount = Math.max(successCount, currentUploadIndex);
  const failedFiles = Object.entries(uploadStatuses)
    .filter(([, s]) => s.status === 'failed')
    .map(([name]) => name);
  const failedCount = failedFiles.length;
  const savedOfflineCount = Object.values(uploadStatuses).filter(s => s.status === 'saved_offline').length;
  const uploadPercentage = total > 0 ? Math.round((displayedSuccessCount / total) * 100) : 0;
  const isUploadDone = uploadPercentage === 100;
  const barClass = uploadPaused ? 'fu-progress-bar-fill--paused' : isUploadDone ? 'fu-progress-bar-fill--done' : 'fu-progress-bar-fill--active';

  return (
    <div className="fu-page">

      <div className="fu-breadcrumb">
        <button className="fu-breadcrumb-back" onClick={() => { window.__appAllowBack = true; window.history.back(); }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <Link to="/eventlist" className="fu-breadcrumb-link">Events</Link>
        <span className="fu-breadcrumb-sep">›</span>
        <Link to="/folders" className="fu-breadcrumb-link">All Folders</Link>
        <span className="fu-breadcrumb-sep">›</span>
        <span className="fu-breadcrumb-current">Upload Photos</span>
      </div>

      <div className="fu-header">
        <div className="fu-header-left">
          <h1>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#F80353" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
            </svg>
            Upload Photos
          </h1>
          <p>{total > 0 ? `${total} photo${total !== 1 ? 's' : ''} selected` : 'Select photos to upload to this folder'}</p>
        </div>
        <button
          className="fu-tour-btn"
          onClick={() => setShowWalkthrough(true)}
          title="Page tour"
        >?</button>
      </div>

      {hasStoredHandles && !uploading && !folderHasBeenUsed && (
        <div className="fu-pending-card">
          <div className="fu-pending-icon-wrap">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#F80353" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
          </div>
          <div className="fu-pending-body">
            <p className="fu-pending-title">Upload Paused</p>
            <p className="fu-pending-sub">
              <strong>{storedHandleCount}</strong> file{storedHandleCount !== 1 ? 's' : ''} from your last session {isOnline ? 'are ready to resume.' : 'will resume when back online.'}
            </p>
            <div className="fu-pending-meta">
              <span className={`fu-pending-dot ${isOnline ? 'fu-pending-dot--online' : 'fu-pending-dot--offline'}`} />
              {isOnline ? 'Connected — ready to upload' : 'No internet connection'}
            </div>
          </div>
          {isOnline && (
            <button className="fu-pending-resume-btn" onClick={() => { setResuming(true); setUploading(true); resumeFromHandles().finally(() => setResuming(false)); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="5 3 19 12 5 21 5 3"/>
              </svg>
              Resume
            </button>
          )}
        </div>
      )}


      {uploadPaused && (
        <div className="fu-banner fu-banner--paused">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <div>
            <strong>Upload paused — no internet</strong>
            Upload will resume automatically when your connection is restored.
          </div>
        </div>
      )}

      {hasStoredPendingUploads && !uploading && (
        <div className="fu-pending-card">
          <div className="fu-pending-icon-wrap">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#F80353" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
          </div>
          <div className="fu-pending-body">
            <p className="fu-pending-title">Upload Paused</p>
            <p className="fu-pending-sub">
              <strong>{storedFileCount}</strong> file{storedFileCount !== 1 ? 's' : ''} from your last session {isOnline ? 'are ready to resume.' : 'will resume when back online.'}
            </p>
            <div className="fu-pending-meta">
              <span className={`fu-pending-dot ${isOnline ? 'fu-pending-dot--online' : 'fu-pending-dot--offline'}`} />
              {isOnline ? 'Connected — ready to upload' : 'No internet connection'}
            </div>
          </div>
          {isOnline && files.length > 0 && (
            <button className="fu-pending-resume-btn" onClick={() => { setHasStoredPendingUploads(false); setResuming(true); setUploading(true); resumeUploads().finally(() => setResuming(false)); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="5 3 19 12 5 21 5 3"/>
              </svg>
              Resume
            </button>
          )}
        </div>
      )}

      {resuming && (
        <div className="fu-banner fu-banner--resuming">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
          </svg>
          <div>
            <strong>Resuming upload…</strong>
            <span> Please wait while we prepare your files for upload.</span>
          </div>
        </div>
      )}

      {!checkingPhotos && galleryImages.length === 0 && !hasStoredHandles && !hasStoredPendingUploads && !uploading && (
        <div className="fu-upload-zone">
          <div className="fu-upload-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
              <path d="M5 21h14"/>
            </svg>
          </div>
          <p className="fu-upload-title">+ Upload Photos</p>
          <p className="fu-upload-sub">Click the button below to select images</p>
          <button className="fu-file-input-btn" onClick={pickFilesWithFSA}>
            Select Photos
          </button>
        </div>
      )}

      {files.length > 0 && (
        <div className="fu-progress-card">
          <div className="fu-progress-header">
            <span className="fu-progress-title">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/><path d="M5 21h14"/>
              </svg>
              Upload Progress
            </span>
            <span className="fu-progress-pct">{uploadPercentage}%</span>
          </div>

          <div className="fu-progress-bar-track">
            <div className={`fu-progress-bar-fill ${barClass}`} style={{ width: `${uploadPercentage}%` }} />
          </div>

          <div className="fu-stats-row">
            <div className="fu-stat fu-stat--success">
              <span className="fu-stat-num">{displayedSuccessCount}</span>
              <span className="fu-stat-label">Uploaded</span>
            </div>
            <div className="fu-stat fu-stat--failed">
              <span className="fu-stat-num">{failedCount}</span>
              <span className="fu-stat-label">Failed</span>
            </div>
            <div className="fu-stat fu-stat--pending">
              <span className="fu-stat-num">{Math.max(0, total - displayedSuccessCount - failedCount)}</span>
              <span className="fu-stat-label">Remaining</span>
            </div>
            {savedOfflineCount > 0 && (
              <div className="fu-stat fu-stat--offline">
                <span className="fu-stat-num">{savedOfflineCount}</span>
                <span className="fu-stat-label">Offline</span>
              </div>
            )}
          </div>

          {failedCount > 0 && (
            <div className="fu-failed-section">
              <div className="fu-failed-actions">
                <button className="fu-retry-btn" onClick={retryFailedUploads} disabled={uploading}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                  </svg>
                  Retry Failed Uploads
                </button>
                <button className="fu-failed-toggle" onClick={() => setShowFailedList(p => !p)}>
                  {showFailedList ? 'Hide' : 'Show'} failed files
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                    style={{ transform: showFailedList ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
              </div>
              {showFailedList && (
                <ul className="fu-failed-list">
                  {failedFiles.map((name, i) => (
                    <li key={i} className="fu-failed-item">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                      </svg>
                      <span className="fu-failed-name">{name}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {successCount > 0 && isUploadDone && (
            <div className="fu-success-banner">
              <p>Upload complete! We're processing your photos — this may take a few moments depending on the number of files.</p>
            </div>
          )}
        </div>
      )}

      {galleryImages.length > 0 && (
        <div className="fu-gallery-section">
          <div className="fu-gallery-header">
            <p className="fu-section-title">Uploaded Photos</p>
            <button className="fu-refresh-btn" onClick={refreshGallery} disabled={refreshingGallery} title="Refresh photos">
              <svg
                width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                style={{ animation: refreshingGallery ? 'fu-spin 0.8s linear infinite' : 'none' }}
              >
                <polyline points="23 4 23 10 17 10"/>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
              </svg>
              {refreshingGallery ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
          <AWSGallery galleryData={galleryImages} />
          {galleryMaxKnownPage > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', margin: '24px 0', flexWrap: 'wrap' }}>
              <button
                onClick={() => goToGalleryPage(galleryPage - 1)}
                disabled={galleryPage === 1}
                style={galleryPageBtnStyle(false, galleryPage === 1)}
              >‹</button>
              {Array.from({ length: galleryMaxKnownPage }, (_, i) => i + 1).map(num => (
                <button
                  key={num}
                  onClick={() => goToGalleryPage(num)}
                  disabled={num === galleryPage}
                  style={galleryPageBtnStyle(num === galleryPage, false)}
                >{num}</button>
              ))}
              <button
                onClick={() => goToGalleryPage(galleryPage + 1)}
                disabled={galleryPage === galleryMaxKnownPage}
                style={galleryPageBtnStyle(false, galleryPage === galleryMaxKnownPage)}
              >›</button>
            </div>
          )}
        </div>
      )}


      {showWalkthrough && (
        <Walkthrough
          steps={UPLOAD_STEPS}
          onDone={() => {
            setShowWalkthrough(false);
            localStorage.setItem('folder_upload_walkthrough_seen', 'true');
          }}
        />
      )}

      <ConnectivityPopup isVisible={showConnectivityPopup} isOnline={isOnline} wasOffline={wasOffline} onRetry={handleConnectivityRetry} />

      {showBuyCreditsPage && (
        <BuyCreditsPage
          onClose={() => setShowBuyCreditsPage(false)}
          onSuccess={() => {
            const vendorId = localStorage.getItem('vendorId');
            if (vendorId) {
              const baseurl = process.env.REACT_APP_API_BASE_URL;
              const apiKey = process.env.REACT_APP_BROWSER_API_KEY;
              fetch(`${baseurl}/vendor?vendor_id=${encodeURIComponent(vendorId)}`, { headers: { 'x-api-key': apiKey } })
                .then(r => r.json())
                .then(d => { const v = d.vendor || d; setCredits(v.photo_credits ?? 0); })
                .catch(() => {});
            }
          }}
          phone={vendorPhone}
        />
      )}

      {showPhotoLimitDialog && (
        <div className="fu-credits-overlay">
          <div className="fu-credits-modal">
            <div className="fu-credits-modal-icon">📷</div>
            <h2>Too Many Photos</h2>
            <p>You selected <strong>{tooManyCount.toLocaleString()} photos</strong>, but the maximum allowed per upload is <strong>1,000 photos</strong>.</p>
            <p className="fu-credits-modal-note">Please deselect some photos and try again.</p>
            <div className="fu-credits-modal-actions">
              <button className="fu-credits-buy-btn" onClick={() => setShowPhotoLimitDialog(false)}>
                OK, Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {showBuyCredits && (
        <div className="fu-credits-overlay">
          <div className="fu-credits-modal">
            <div className="fu-credits-modal-icon">🪙</div>
            <h2>Not Enough Credits</h2>
            <p>You selected <strong>{selectedPhotoCount} photos</strong> but only have <strong>{credits} credit{credits !== 1 ? 's' : ''}</strong> remaining.</p>
            <p className="fu-credits-modal-note">1 credit = 1 photo</p>
            <div className="fu-credits-modal-actions">
              <button className="fu-credits-cancel-btn" onClick={() => setShowBuyCredits(false)}>
                Cancel
              </button>
              <button className="fu-credits-buy-btn" onClick={() => { setShowBuyCredits(false); setShowBuyCreditsPage(true); }}>
                Buy Credits
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function galleryPageBtnStyle(isActive, isDisabled) {
  return {
    minWidth: '36px',
    height: '36px',
    padding: '0 10px',
    borderRadius: '6px',
    border: isActive ? '2px solid #F80353' : '1px solid #ddd',
    background: isActive ? '#F80353' : '#fff',
    color: isActive ? '#fff' : isDisabled ? '#bbb' : '#333',
    fontWeight: isActive ? '600' : '400',
    fontSize: '14px',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.15s',
  };
}
